import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import gql from 'graphql-tag';

import { useMutation, useLazyQuery } from '@apollo/client';
import { DropzoneDialog } from 'mui-file-dropzone';
import makeStyles from '@mui/styles/makeStyles';
import Button from '../../components/CustomButtons/Button';

import Grid2 from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { DocumentContext } from '../../contexts/DocumentContext';
import styles from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle.js';
import { rejectHiddenDocumentTypeOptions } from '../../services/pcwService';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';

import _ from 'lodash';

import { documentsByKey } from '../../constants/common';
export const GET_CREDIT_SUBMISSIONS = gql`
  query CreditSubmissionsByDynamicsVOId($id: ID!, $display: String) {
    creditSubmissionsByDynamicsVOId(id: $id, display: $display) {
      creditSubmissionId
      creditSubId
      submissionDate
      decision
      lenderName
      amountRequested
      approvedAmount
      notes
      contingencies
      approvedRate
      creditSubStage
      apiApplicationId
      apiResult
      apiExpiredCredentials
      apiDetail
      lenderProfileId
      lenderChannelCode
      lenderTypeCode
      draft
      creditSubStage
      transactionSummary
      mappedValues
      source
      submittedBy
      lenderContactInfo {
        id
        email
        dynamicsContactId
        fullName
        title
        phoneNumber
        accountId
      }
      fastTrackResults {
        approvedBy
        fastTrackId
        maxApprovalAmount
        rate
        inputs {
          key
          value
        }
        outputs {
          key
          value
        }
      }
    }
  }
`;

const getModalStyle = () => {
  return {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50vw',
  };
};

const UPLOAD_FILE = gql`
  mutation ($input: UpsertDocumentInput!, $file: Upload) {
    upsertDocument(input: $input, file: $file)
  }
`;

export default function FileUploadModal(props) {
  const userProfile = useSelector(state => state.userProfile);
  // Use the prop directly (may be undefined); stabilise with useMemo so the
  // array reference does not change on every render when the prop is absent.
  const lenderContactInfo = React.useMemo(() => props.lenderContactInfo ?? [], [props.lenderContactInfo]);
  const [availableLenderContacts, setAvailableLenderContacts] = useState(lenderContactInfo);
  const [creditSubmissionsAvailableForChoosing, setCreditSubmissionsAvailableForChoosing] = useState([]);
  const [targetCreditSubmission, setTargetCreditSubmission] = useState(_.get(props, 'creditSubmissionId', ''));
  const [availableDealerContacts, setAvailableDealerContacts] = useState([]);
  const useUiStyles = makeStyles(styles);

  const useStyles = makeStyles(theme => ({
    button: {
      margin: theme.spacing(0, 2),
    },
    paper: {
      position: 'absolute',
      width: 600,
      backgroundColor: theme.palette.background.paper,
      border: '2px solid #000',
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'wrap',
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 240,
    },
    primaryBorderColor: {
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '& .MuiInputLabel-shrink': {
        color: theme.palette.primary.main,
      },
    },
    hideDropZone: {
      display: 'none',
    },
    showDropZone: {
      display: 'block',
    },
  }));

  const [getCreditSubmissions, { data: creditSubmissionsData }] = useLazyQuery(GET_CREDIT_SUBMISSIONS);

  // Extract stable primitives to use as effect dependencies instead of the whole
  // props object, which gets a new reference on every parent render and would
  // cause an infinite loop.
  const salesRep = props.vo?.salesRepresentative ?? null;
  const salesManager = props.vo?.salesManager ?? null;
  const financeManager = props.vo?.financeManager ?? null;
  const creditManager = props.vo?.creditManager ?? null;
  const dynamicsVendorOpportunityId = props.vo?.dynamicsVendorOpportunityId;
  const creditSubmissionDisplay = props.vp?.creditSubmissionDisplay;

  useEffect(() => {
    setAvailableDealerContacts(
      _.uniqBy(
        [salesRep, salesManager, financeManager, creditManager].filter(x => x !== null),
        'id'
      )
    );
    if (dynamicsVendorOpportunityId) {
      const variables = { id: dynamicsVendorOpportunityId };
      if (creditSubmissionDisplay) variables.display = creditSubmissionDisplay;
      getCreditSubmissions({ variables });
    }
  }, [
    dynamicsVendorOpportunityId,
    creditSubmissionDisplay,
    salesRep,
    salesManager,
    financeManager,
    creditManager,
    getCreditSubmissions,
  ]);

  useEffect(() => {
    if (lenderContactInfo && lenderContactInfo.length > 0) {
      setAvailableLenderContacts(lenderContactInfo);
    } else {
      const dataAvailable = _.get(creditSubmissionsData, 'creditSubmissionsByDynamicsVOId', []);
      if (dataAvailable && dataAvailable.length > 0) {
        const filteredData = dataAvailable
          .map(cs => {
            if (cs) return cs;
            return null;
          })
          .filter(x => x !== null);

        setCreditSubmissionsAvailableForChoosing(filteredData);

        const lenderContacts = _.flatten(filteredData.map(cs => cs.lenderContactInfo).filter(x => x !== null));

        if (lenderContacts.length > 0) {
          setAvailableLenderContacts(_.uniqBy(lenderContacts, 'id'));
        }
      }
    }
  }, [creditSubmissionsData, lenderContactInfo]);

  const classes = { ...useStyles(), ...useUiStyles() };
  const [modalStyle] = useState(getModalStyle);
  const [openErrorDialogue, setOpenErrorDialogue] = useState(false);

  // Support both controlled (open/onClose from parent) and uncontrolled modes.
  const isControlled = props.open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const openUploadZone = isControlled ? props.open : internalOpen;

  const [mutationError, setMutationError] = useState('');
  const [fileType, setFileType] = useState(props.fixedDocType ?? '');
  const [relatedTo, setRelatedTo] = useState(
    props.relatedTo || _.get(props, 'creditSubmissionId', '') ? 'submission' : 'application'
  );
  const [fileDescription, setFileDescription] = useState('');
  const [openFileDescriptionInputModal, setOpenFileDescriptionInputModal] = useState(false);
  const [openInvoiceQuoteUploadModal, setOpenInvoiceQuoteUploadModal] = useState(false);
  const [sendNotifications, setSendNotifications] = useState(true);
  const [selectedDealerContact, setSelectedDealerContact] = useState([]);
  const [selectedLenderContact, setSelectedLenderContact] = useState([]);
  const [fileSelected, setFileSelected] = useState(false);
  const [fileInput] = useMutation(UPLOAD_FILE, {
    context: { authRequired: true },
  });
  const documentContext = useContext(DocumentContext);
  const graphqlErrorMessage = {
    duplicateMessage:
      'GraphQL error: duplicate key value violates unique constraint "document_vendor_opportunity_id_doc_name_key"',
  };

  const errorDialogue = (
    <div style={modalStyle} className={classes.paper}>
      <h2 id="simple-modal-title">Error</h2>
      <p id="simple-modal-description">
        {!!mutationError && mutationError.message === graphqlErrorMessage.duplicateMessage
          ? 'This file is already uploaded'
          : ''}
      </p>
    </div>
  );

  const handleOpenErrorDialogue = () => setOpenErrorDialogue(true);
  const handleCloseErrorDialogue = () => setOpenErrorDialogue(false);
  const handleOpenUploadZone = () => {
    if (!isControlled) setInternalOpen(true);
  };
  const handleCloseUploadZone = () => {
    if (isControlled) props.onClose?.();
    else setInternalOpen(false);
  };
  const handleOpenFileDescriptionInputModal = () => setOpenFileDescriptionInputModal(true);
  const handleCloseFileDescriptionInputModal = () => setOpenFileDescriptionInputModal(false);
  const handleOpenInvoiceQuoteUploadModal = () => setOpenInvoiceQuoteUploadModal(true);
  const handleCloseInvoiceQuoteUploadModal = () => setOpenInvoiceQuoteUploadModal(false);

  const documentTypeOptionsBase = [documentsByKey.quote, documentsByKey.invoice];

  const documentTypeOptionsFull = [
    documentsByKey.quote,
    documentsByKey.invoice,
    documentsByKey.buyers_order,
    documentsByKey.decision_letter,
    documentsByKey.insurance_certificate,
    documentsByKey.driver_license,
    documentsByKey.credit_report,
    documentsByKey.bank_statements,
    documentsByKey.tax_returns,
    documentsByKey.equipment_specs,
    documentsByKey.sales_tax_exemption,
    documentsByKey.credit_app_scan,
    documentsByKey.financial,
    documentsByKey.other,
  ];

  let documentTypeOptions = props.equipmentUpload ? documentTypeOptionsBase : documentTypeOptionsFull;
  documentTypeOptions = rejectHiddenDocumentTypeOptions(
    props.documentsPortalConfiguration,
    documentTypeOptions,
    userProfile.vendorContactRole
  );
  const handleSaveFile = async uploadFile => {
    if (uploadFile.length === 0) setMutationError('No File Selected');

    try {
      let variables = {
        input: {
          vendorOpportunityId: props.vo.vendorOpportunityId,
          source: _.get(uploadFile, '[0].name', fileDescription),
          docType: fileType,
          docDescription: fileDescription,
          createdBy: 'testing',
          docName: _.get(uploadFile, '[0].name', fileDescription),
          notifyUserProfileIds: selectedDealerContact.concat(selectedLenderContact),
          ...(props.aiEnabledParsing ? { aiEnabledParsing: true } : {}),
        },
        file: uploadFile[0],
      };
      if (targetCreditSubmission) {
        variables.input = {
          ...variables.input,
          creditSubId: targetCreditSubmission,
        };
      }

      const response = await fileInput({
        variables,
      });

      await documentContext.setLastDocument(response.data.upsertDocument);
      setFileType('');
      setFileDescription('');
    } catch (err) {
      setMutationError(err);
      handleOpenErrorDialogue();
      setFileType('');
      setFileDescription('');
    }
  };

  const handleCheckboxChange = event => {
    setSendNotifications(event.target.checked);
    if (event.target.checked) {
      console.log('Notifications activated');
    } else {
      console.log('Notifications deactivated');
    }
  };

  const handleChangeDocDescription = event => {
    setFileDescription(event.target.value);
  };

  const handleFileDrop = file => {
    if (file.length > 0) {
      setFileSelected(true);
      handleSaveFile(file);
    }
  };

  return (
    <div>
      {/* In uncontrolled mode render the built-in trigger button. */}
      {!isControlled && (
        <Button color="primary" size="sm" onClick={handleOpenUploadZone}>
          {props.buttonDescription ? props.buttonDescription() : ''}
        </Button>
      )}

      <DropzoneDialog
        dropzoneClass={openFileDescriptionInputModal ? classes.hideDropZone : classes.showDropZone}
        open={openUploadZone}
        onDelete={() => {
          setFileSelected(false);
          setFileType('');
          setFileDescription('');
          setOpenFileDescriptionInputModal(false);
        }}
        onDrop={
          props.equipmentUpload === true ? handleOpenInvoiceQuoteUploadModal : handleOpenFileDescriptionInputModal
        }
        dialogTitle={
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Grid2 container>
                <Grid2 size={12}>
                  <h6>Upload File</h6>
                </Grid2>
                <Grid2 size={12}>
                  <FormControlLabel
                    control={<Checkbox checked={sendNotifications} onChange={handleCheckboxChange} size="small" />}
                    label="Send notifications"
                  />
                </Grid2>
              </Grid2>
            </Grid2>
            <Grid2 size={12}>
              {sendNotifications && (
                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <Autocomplete
                      multiple
                      size="small"
                      onChange={(event, val) => {
                        setSelectedDealerContact(val.map(obj => obj.id));
                      }}
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value;
                      }}
                      getOptionLabel={option => option.fullName || ''}
                      value={availableDealerContacts.filter(val => val && selectedDealerContact.includes(val.id))}
                      options={availableDealerContacts.filter(val => val && !selectedDealerContact.includes(val.id))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Dealer Contact to Notify"
                          variant="outlined"
                          formControlProps={{
                            fullWidth: true,
                          }}
                          placeholder=""
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <Autocomplete
                      multiple
                      size="small"
                      getOptionLabel={option => option.fullName || ''}
                      value={availableLenderContacts.filter(val => val && selectedLenderContact.includes(val.id))}
                      options={availableLenderContacts.filter(val => val && !selectedLenderContact.includes(val.id))}
                      onChange={(event, val) => {
                        setSelectedLenderContact(val.map(obj => obj.id));
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Lender Contact to Notify"
                          variant="outlined"
                          formControlProps={{
                            fullWidth: true,
                          }}
                          placeholder=""
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              )}
            </Grid2>
            <Grid2 size={12}>
              <Grid2 container spacing={4}>
                {openFileDescriptionInputModal ? (
                  <Grid2 size={12}>
                    <form>
                      <Grid2 container spacing={2}>
                        <Grid2 size={6}>
                          <FormControl required fullWidth>
                            <InputLabel htmlFor="demo-dialog-native">Document Type</InputLabel>
                            <Select
                              color="primary"
                              value={fileType}
                              label="Document Type"
                              variant="outlined"
                              disabled={!!props.fixedDocType}
                              onChange={e => {
                                setFileType(e.target.value);
                              }}
                              inputProps={{ name: 'lenderSelect', id: 'lender-select' }}
                            >
                              {documentTypeOptions.map((item, index) => (
                                <MenuItem key={index} value={item.value}>
                                  {item.text}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid2>
                        <Grid2 size={6}>
                          <FormControl required fullWidth>
                            <InputLabel htmlFor="demo-dialog-native">Related To</InputLabel>
                            <Select
                              color="primary"
                              value={relatedTo}
                              variant="outlined"
                              label="Related To"
                              disabled={!!_.get(props, 'creditSubmissionId', '') || !!props.fixedDocType}
                              onChange={e => {
                                setRelatedTo(e.target.value);
                              }}
                              inputProps={{ name: 'relatedTo', id: 'related-to' }}
                            >
                              <MenuItem key={0} value={'application'}>
                                Application
                              </MenuItem>
                              <MenuItem key={1} value={'submission'}>
                                Submission
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid2>
                        <Grid2 size={12}>
                          {relatedTo === 'submission' && (
                            <FormControl required fullWidth>
                              <InputLabel htmlFor="demo-dialog-native">Select Submission</InputLabel>
                              <Select
                                color="primary"
                                variant="outlined"
                                value={targetCreditSubmission}
                                onChange={e => {
                                  if (e.target.value) {
                                    setTargetCreditSubmission(e.target.value);
                                  }
                                }}
                                disabled={!!_.get(props, 'creditSubmissionId', '')}
                                label="Select Submission"
                                inputProps={{ name: 'creditSubmissionSelected', id: 'creditSubId-select' }}
                              >
                                {_.isEmpty(creditSubmissionsAvailableForChoosing) ? (
                                  <MenuItem value="">No Credit Submissions Available</MenuItem>
                                ) : (
                                  creditSubmissionsAvailableForChoosing.map(cs => {
                                    return (
                                      <MenuItem key={cs.creditSubId} value={cs.creditSubId}>
                                        {cs.lenderName} - Submitted By {cs.submittedBy} @ {cs.submissionDate}
                                      </MenuItem>
                                    );
                                  })
                                )}
                              </Select>
                            </FormControl>
                          )}
                        </Grid2>
                        <Grid2 size={12}>
                          <FormControl fullWidth>
                            <TextField
                              id="outlined-multiline-static1"
                              label="Document Description"
                              multiline
                              rows={4}
                              value={fileDescription}
                              className={classes.primaryBorderColor}
                              variant="outlined"
                              onChange={handleChangeDocDescription}
                              required={fileType === 'other'}
                              error={fileType === 'other'}
                              helperText={fileType === 'other' ? 'Please describe your document' : false}
                            />
                          </FormControl>
                        </Grid2>
                      </Grid2>
                    </form>
                  </Grid2>
                ) : null}
              </Grid2>
            </Grid2>
          </Grid2>
        }
        onSave={file => {
          handleFileDrop(file);
          handleCloseUploadZone();
          setFileSelected(false);
          setSelectedDealerContact([]);
          setSelectedLenderContact([]);
          setOpenInvoiceQuoteUploadModal(false);
          props.equipmentUpload === true
            ? handleCloseInvoiceQuoteUploadModal()
            : handleCloseFileDescriptionInputModal();
          setFileDescription('');
        }}
        onClose={() => {
          handleCloseUploadZone();
          setFileSelected(false);
          setSelectedDealerContact([]);
          setSelectedLenderContact([]);
          setOpenInvoiceQuoteUploadModal(false);
          props.equipmentUpload === true
            ? handleCloseInvoiceQuoteUploadModal()
            : handleCloseFileDescriptionInputModal();
          setFileDescription('');
        }}
        filesLimit={1}
        dropzoneText={'Drag & drop or Choose File'}
        showPreviews={true}
        maxFileSize={25000000}
      ></DropzoneDialog>

      {/* <Modal
        open={openErrorDialogue}
        onClose={handleCloseErrorDialogue}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description">
        {errorDialogue}
      </Modal> */}
    </div>
  );
}
